/**
 * Custom fetch mutator for the orval-generated LiftAI SDK (AMP-312).
 *
 * orval's generated operations call `customFetch<T>(url, init)`; this module
 * supplies the cross-cutting concerns the hand-written `LiftAPIClient` used to
 * handle inline: base URL, bearer auth, JSON content-type, and uniform error
 * wrapping via {@link LiftAPIClientError}.
 *
 * Auth/host/fetch are injected once via {@link configureLiftApi} rather than
 * per-call, mirroring the dependency-injection config the old client took in
 * its constructor.
 *
 * This file is hand-written and lives outside `generated/` so orval's `clean`
 * step never removes it.
 */

/** Error thrown for non-2xx responses, mirroring the legacy client's shape. */
export class LiftAPIClientError extends Error {
  constructor(
    message: string,
    public readonly context: {
      status: number;
      statusText: string;
      data: unknown;
    },
    { cause }: { cause: unknown },
  ) {
    super(message, { cause });
    this.context = context;
  }
}

export interface LiftApiConfig {
  /** Resolves the bearer token to send (a Django-issued access token). */
  authToken: () => Promise<string>;
  /**
   * Called once when a request that carried `authToken()`'s token gets a 401.
   * Resolves the token to retry with, or throws to fail the request.
   */
  refreshAuthToken?: (rejectedToken: string) => Promise<string>;
  /** API origin, e.g. `https://api.liftai.com`. The schema server prefix (`/api/v1`) is part of generated paths. */
  apiHost: string;
  /** Injectable fetch (defaults to global fetch) for tests/SSR. */
  fetch?: typeof fetch;
}

let config: LiftApiConfig | null = null;

/** Configure the generated SDK once at app startup. */
export function configureLiftApi(next: LiftApiConfig): void {
  config = next;
}

function requireConfig(): LiftApiConfig {
  if (!config) {
    throw new Error(
      'LiftAI SDK is not configured. Call configureLiftApi({ authToken, apiHost }) before use.',
    );
  }
  return config;
}

// The details DRF gives a missing or refused credential. An API from before the
// authentication classes were reordered answers them with 403, not 401 (AMP-770).
const AUTHENTICATION_FAILURE_DETAILS = new Set([
  'Authentication credentials were not provided.',
  'Unable to authenticate with the provided credentials.',
]);

/**
 * Whether `response` refused the credential, as opposed to refusing the action:
 * a 401, or a 403 carrying one of DRF's authentication-failure details. A
 * permission 403 is not one. Reads a clone, so the body stays unread.
 */
export const isAuthenticationFailure = async (response: Response): Promise<boolean> => {
  if (response.status === 401) {
    return true;
  }
  if (response.status !== 403) {
    return false;
  }
  try {
    const body = (await response.clone().json()) as { detail?: unknown } | null;
    return typeof body?.detail === 'string' && AUTHENTICATION_FAILURE_DETAILS.has(body.detail);
  } catch {
    return false;
  }
};

/**
 * orval `client: 'fetch'` mutator. Returns the parsed JSON body typed as `T`.
 */
export const customFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const { authToken, refreshAuthToken, apiHost, fetch: fetchImpl = fetch } = requireConfig();

  const headers = new Headers(options.headers);
  // A caller-supplied Authorization header (including an empty one for public
  // endpoints) is sent as-is and never refreshed.
  let sentToken: string | null = null;
  if (!headers.has('Authorization')) {
    sentToken = await authToken();
    headers.set('Authorization', `Bearer ${sentToken}`);
  }
  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response = await fetchImpl(`${apiHost}${url}`, { ...options, headers });

  // One retry, never a loop: a second refusal is reported like any other failure.
  if (sentToken !== null && refreshAuthToken && (await isAuthenticationFailure(response))) {
    headers.set('Authorization', `Bearer ${await refreshAuthToken(sentToken)}`);
    response = await fetchImpl(`${apiHost}${url}`, { ...options, headers });
  }

  if (!response.ok) {
    let data: unknown = null;
    let cause: unknown = null;
    try {
      data = await response.json();
    } catch (e) {
      cause = e;
    }
    throw new LiftAPIClientError(
      `Request to ${url} failed with ${response.status}`,
      { status: response.status, statusText: response.statusText, data },
      { cause },
    );
  }

  // orval's `client: 'fetch'` generates response types as
  // `{ data: T, status: number } & { headers: Headers }` and returns the
  // mutator's result directly, so the mutator must produce that wrapper — not
  // the raw body. Returning the bare body would make every `response.data`
  // access undefined at runtime.
  // Decode by content type: JSON → parsed object; text/* (or unmarked) → string;
  // anything else (binary downloads like application/pdf, which orval types as
  // `Blob`) → Blob. Decoding a binary body as text would corrupt it.
  const contentType = response.headers.get('Content-Type') ?? '';
  let data: unknown;
  if (response.status === 204) {
    data = undefined;
  } else if (contentType.includes('application/json')) {
    data = await response.json();
  } else if (contentType.startsWith('text/') || contentType === '') {
    data = await response.text();
  } else {
    data = await response.blob();
  }

  return { data, status: response.status, headers: response.headers } as T;
};

export default customFetch;
